import React from 'react';
import { Link } from 'react-router-dom';
import Carousel from '../components/Carousel';
import NewsSection from '../components/NewsSection';
import FeaturedSection from '../components/FeaturedSection';
import SocialLinks from '../components/SocialLinks';
import { usePageLastUpdated } from '../hooks/usePageLastUpdated';
import './HomePage.css';

// Optional: set to override fallback (page JS file timestamp)
const PAGE_LAST_UPDATED = undefined; // e.g. { dateTime: '2025-12-23', displayDate: 'December 23, 2025' }

const HomePage = () => {
  usePageLastUpdated(PAGE_LAST_UPDATED);
  // Carousel slides data
  const carouselSlides = [
    {
      backgroundImage: '/images/banner_me.png',
      content: (
        <>
          <p>
            <a href="/bio" title="Go to my bio web page.">I</a> am a product lead at{' '}
            <a
              href="https://www.adevinta.com/"
              title="Go to the Adevinta web site."
              target="_blank"
              rel="noopener"
              className="external"
            >
              Adevinta
            </a>
            , the global online classifieds specialist, at their software development hub in{' '}
            <a
              href="https://www.barcelona.cat/"
              title="Go to the web site of Barcelona city hall."
              target="_blank"
              rel="noopener"
              className="external"
            >
              Barcelona
            </a>
            ,{' '}
            <a
              href="https://www.gencat.cat/"
              title="Go to the web site of Catalonia regional government."
              target="_blank"
              rel="noopener"
              className="external"
            >
              Catalonia
            </a>
            ,{' '}
            <a
              href="https://www.spain.info/"
              title="Go to the web site of Spain tourist office."
              target="_blank"
              rel="noopener"
              className="external"
            >
              Spain
            </a>
            .
          </p>
          <p>
            My <Link to="/projects" title="See my projects.">work</Link> focuses on product
            development, software and cloud computing architectures, computer networks and Internet
            technologies.
          </p>
          <p>
            <a
              className="btn btn--primary"
              role="button"
              href="/bio"
              title="More information about me."
            >
              Read more...
            </a>
          </p>
        </>
      ),
    },
    {
      backgroundImage: '/images/banner_openstack_austin.png',
      content: (
        <>
          <p>
            The first{' '}
            <a
              href="https://www.openstack.org/summit/austin-2016/"
              title="Go to the summit."
              target="_blank"
              rel="noopener"
              className="external"
            >
              OpenStack Summit
            </a>{' '}
            of 2016 was organized in Austin, Texas between April 25-29.
          </p>
          <p>
            At the week-long conference I presented a{' '}
            <a
              href="https://www.openstack.org/summit/austin-2016/summit-schedule/events/8827"
              title="See the summit session information."
              target="_blank"
              rel="noopener"
              className="external"
            >
              technical session
            </a>{' '}
            on how to{' '}
            <a
              href="/events/openstackaustin2016.html"
              title="Download the slides"
              target="_blank"
              rel="noopener"
            >
              containerize
            </a>{' '}
            your virtual network functions with{' '}
            <a
              href="https://www.openstack.org/"
              title="Go to the OpenStack project."
              target="_blank"
              rel="noopener"
              className="external"
            >
              OpenStack
            </a>
            ,{' '}
            <a
              href="https://wiki.openstack.org/wiki/Neutron"
              title="Go to the Neutron project wiki."
              rel="noopener"
              className="external"
            >
              Neutron
            </a>{' '}
            and{' '}
            <a
              href="https://www.midonet.org/"
              target="_blank"
              rel="noopener"
              className="external"
            >
              MidoNet
            </a>
            .
          </p>
          <p>
            <a
              className="btn btn--primary"
              role="button"
              href="/events/openstackaustin2016.html"
              title="See the event."
            >
              See the event...
            </a>{' '}
            <a
              className="btn btn--primary"
              role="button"
              href="/download/OpenStackAustin2016.pdf"
              title="Download the slides."
              target="_blank"
              rel="noopener"
            >
              Download the slides...
            </a>
          </p>
        </>
      ),
    },
    {
      backgroundImage: '/images/banner_openstack_tokyo.png',
      content: (
        <>
          <p>
            The second{' '}
            <a
              href="https://www.openstack.org/summit/tokyo-2015/"
              title="Go to the summit."
              target="_blank"
              rel="noopener"
              className="external"
            >
              OpenStack Summit
            </a>{' '}
            of 2015 was organized in Tokyo, between October 27-30.
          </p>
          <p>
            The week-long conference is a great opportunity to connect with the{' '}
            <a
              href="https://www.openstack.org/"
              title="See the OpenStack project."
              target="_blank"
              rel="noopener"
              className="external"
            >
              OpenStack
            </a>{' '}
            community, onboarding to{' '}
            <a
              href="https://www.openstack.org/community/"
              title="Check out the OpenStack community."
              target="_blank"
              rel="noopener"
              className="external"
            >
              contributing
            </a>{' '}
            to the project, and{' '}
            <a
              href="https://www.openstack.org/summit/tokyo-2015/videos/"
              title="See the summit keynotes and talks."
              target="_blank"
              rel="noopener"
              className="external"
            >
              learn
            </a>{' '}
            about the latest trends in cloud computing from project and industry leaders.
          </p>
          <p>
            <a
              className="btn btn--primary"
              role="button"
              href="/events/openstacktokyo2015.html"
              title="See the event."
            >
              See the event...
            </a>
          </p>
        </>
      ),
    },
    {
      backgroundImage: '/images/banner_fosdem_15.png',
      content: (
        <>
          <p>The annual event for open-source software developers.</p>
          <p>
            At FOSDEM'15 organized in Brussels on January 30-31, 2015,{' '}
            <a
              href="https://twitter.com/kalymero_"
              title="Follow Antonio on Twitter"
              target="_blank"
              rel="noopener"
              className="external"
            >
              Antonio Sagliocco
            </a>{' '}
            and I presented MidoNet 101: a beginner's introduction to the{' '}
            <a
              href="http://www.midonet.org/"
              title="Check it out"
              target="_blank"
              rel="noopener"
              className="external"
            >
              MidoNet
            </a>{' '}
            SDN.
          </p>
          <p>
            <a
              className="btn btn--primary"
              role="button"
              href="/events/fosdem2015.html"
              title="See the event."
            >
              See the event...
            </a>
          </p>
        </>
      ),
    },
    {
      backgroundImage: '/images/banner_planetlab.png',
      content: (
        <>
          <p>The software that makes it easy to manage your PlanetLab experiments.</p>
          <p>
            The PlanetLab Manager is a desktop application that allows you to configure the PlanetLab
            site, nodes and slices for your experiments. Using the integrated secure shell (SSH)
            console you can run commands directly on the PlanetLab nodes.
          </p>
          <p>
            <a
              className="btn btn--primary"
              role="button"
              href="/projects/inetanalytics/planetlab/index.html"
              title="Go to the PlanetLab Manager web page."
            >
              More information...
            </a>
          </p>
        </>
      ),
    },
  ];

  // News items data
  const newsItems = [
    {
      date: '2017-11-07',
      dateDisplay: 'November 7, 2017',
      content:
        'Started as a developer in the engineering productivity team at the <a href="http://www.schibsted.com/" title="Go to the Schibsted Media Group web site." target="_blank" rel="noopener" class="external">Schibsted Media Group</a> while continuing as a part-time cloud architecture consultant at the network virtualization company <a href="http://www.schibsted.com/" title="Go to the Midokura web site." target="_blank" rel="noopener" class="external">Midokura</a>.',
    },
    {
      date: '2015-04-28',
      dateDisplay: 'April 28, 2015',
      content:
        'Talk on the <a href="/events/openstackaustin2016.html" title="Read more about the talk.">containerizing network services</a> in <a href="https://www.openstack.org/" target="_blank" rel="noopener" class="external">OpenStack</a> at the <a href="https://fosdem.org/" title="Go to the conference web site." target="_blank" rel="noopener" class="external">OpenStack Summit</a>, organized in Austin, Texas between April 25 - 29, 2015.',
    },
    {
      date: '2015-01-30',
      dateDisplay: 'January 30, 2015',
      content:
        'Talk on the <a href="/events/fosdem2015.html" title="Read more about the talk.">MidoNet</a> SDN at <a href="https://fosdem.org/" title="Go to the conference web site." target="_blank" rel="noopener" class="external">FOSDEM\'15</a> software developers\' conference, organized in Brussels, Belgium between January 30 - 31, 2015.',
    },
    {
      date: '2014-07-04',
      dateDisplay: 'July 4, 2014',
      content:
        'Our research paper <a href="/papers/eunice2014.html" title="Read more about the paper.">Revealing Hidden Interconnections between Access ISPs and Content Providers</a> has been accepted at the <a href="http://conferences.telecom-bretagne.eu/eunice2014/" title="Go to the conference web site." target="_blank" rel="noopener" class="external">20th Eunice Open European Summer School and Conference</a>, organized in Rennes, France between September 1 - 5, 2014.',
    },
  ];

  // Featured apps data
  const featuredApps = [
    {
      image: '/images/planetlab.png',
      imageAlt: 'PlanetLab Manager application interface',
      title: 'PlanetLab Manager',
      description:
        'The software that makes it easy to manage your PlanetLab experiments. The PlanetLab Manager is a desktop application that allows you to configure the PlanetLab nodes and slices for your experiments.',
      link: '/projects/inetanalytics/planetlab/index.html',
      buttonText: 'Check It Out',
    },
  ];

  // Featured code data
  const featuredCode = [
    {
      image: '/research/advanced_matlab_boxplot/feature.png',
      imageAlt: 'Advanced Matlab box plot visualization',
      title: 'Advanced Box Plot for Matlab Power Users',
      description:
        'To illustrate the statistical properties of large data sets, Matlab includes a builtin box plot function. Although this function is a suitable tool in everyday use, some circumstances may require a more powerful...',
      link: '/research/advanced_matlab_boxplot/',
      buttonText: 'Read Article',
    },
    {
      image: '/research/latex_in_matlab_ticks/feature.png',
      imageAlt: 'LaTeX formatting in Matlab plot tick labels',
      title: 'Adding LaTeX to Plot Tick Labels in Matlab',
      description:
        'Matlab plots generally support LaTeX markup for the various components, including axes labels, legends, annotations, etc. However, one of the few exceptions are the axis tick labels, that is the data values alongside each...',
      link: '/research/latex_in_matlab_ticks/',
      buttonText: 'Read Article',
    },
  ];

  // Social links data
  const socialLinks = [
    {
      type: 'linkedin',
      url: 'https://www.linkedin.com/in/alexbikfalvi',
      label: 'LinkedIn',
      accessKey: 'L',
      title: 'My professional profile on LinkedIn',
      ariaLabel: 'LinkedIn profile',
    },
    {
      type: 'github',
      url: 'https://www.github.com/alexbikfalvi',
      label: 'Github',
      accessKey: 'K',
      title: 'Go check my code on GitHub.',
      ariaLabel: 'GitHub profile',
    },
    {
      type: 'flickr',
      url: 'https://www.flickr.com/photos/alexbikfalvi',
      label: 'Flickr',
      accessKey: 'F',
      title: 'Go to see my photos on Flickr',
      ariaLabel: 'Flickr photos',
    },
    {
      type: 'facebook',
      url: 'https://www.facebook.com/alexbikfalvi',
      label: 'Facebook',
      title: 'If you know me, be my friend on Facebook.',
      ariaLabel: 'Facebook profile',
    },
    {
      type: 'twitter',
      url: 'https://twitter.com/alexbikfalvi',
      label: 'Twitter',
      accessKey: 'T',
      title: 'Read my thoughts on Twitter.',
      ariaLabel: 'Twitter profile',
    },
    {
      type: 'lastfm',
      url: 'https://www.last.fm/user/alexbikfalvi',
      label: 'last.fm',
      accessKey: 'M',
      title: 'What I am listening to on last.fm',
      ariaLabel: 'Last.fm profile',
    },
  ];

  return (
    <>
      <Carousel slides={carouselSlides} />
      <NewsSection newsItems={newsItems} />
      <div className="content-sections">
        <FeaturedSection
          title="Featured Apps"
          id="featured-apps-title"
          items={featuredApps}
        />
        <FeaturedSection
          title="Featured Code"
          id="featured-code-title"
          items={featuredCode}
        />
        <SocialLinks links={socialLinks} />
      </div>
    </>
  );
};

export default HomePage;
